import { dependentModellingDateOfBirth, dependentName, isoDate, pence } from '@cf/shared';
import { resolveGoalAnchorYear } from './goals.js';
import { resolveAssumptions } from './assumptions.js';
/**
 * Pure wire→engine mapping for the v2 household projection. The contract has
 * already validated shapes and integer pence; this layer brands the monetary
 * fields (`pence`) and dates, and resolves the assumption SPEC through the
 * engine's `resolveAssumptions` front door so the run carries full
 * provenance. The engine re-validates everything again (fail-closed twice).
 */
/** Drop explicitly-undefined entries so exact-optional target types accept the record. */
function cleanBpsRecord(record) {
    if (record === undefined)
        return undefined;
    const out = {};
    for (const [k, v] of Object.entries(record)) {
        if (v !== undefined)
            out[k] = v;
    }
    return out;
}
/** Wire schedule step → engine step (optionals stripped, delta record cleaned). */
function mapScheduleStep(s) {
    const growthDeltaBpsByWrapper = cleanBpsRecord(s.growthDeltaBpsByWrapper);
    return {
        id: s.id,
        fromYear: s.fromYear,
        ...(s.label !== undefined ? { label: s.label } : {}),
        ...(s.toYear !== undefined ? { toYear: s.toYear } : {}),
        ...(s.inflationBps !== undefined ? { inflationBps: s.inflationBps } : {}),
        ...(s.wageGrowthBps !== undefined ? { wageGrowthBps: s.wageGrowthBps } : {}),
        ...(s.rpiBps !== undefined ? { rpiBps: s.rpiBps } : {}),
        ...(growthDeltaBpsByWrapper !== undefined ? { growthDeltaBpsByWrapper } : {}),
    };
}
function toFirmPresetTable(presets) {
    if (presets === undefined)
        return undefined;
    return {
        ...(presets.conservative !== null ? { conservative: presets.conservative } : {}),
        ...(presets.moderate !== null ? { moderate: presets.moderate } : {}),
        ...(presets.optimistic !== null ? { optimistic: presets.optimistic } : {}),
    };
}
export function buildAssumptionSpec(wire, options = {}) {
    const growthByWrapper = cleanBpsRecord(wire.overrides?.growthByWrapper);
    const yieldByWrapper = cleanBpsRecord(wire.overrides?.yieldByWrapper);
    const feeByWrapper = cleanBpsRecord(wire.overrides?.feeByWrapper);
    const firmPresets = toFirmPresetTable(options.firmPresets);
    const pinnedSet = wire.setVersionId === undefined
        ? undefined
        : options.pinnedSets?.find((p) => p.versionId === wire.setVersionId);
    if (wire.setVersionId !== undefined && pinnedSet === undefined) {
        // Fail loudly: silently falling back to a product or firm bundle would
        // produce plausible figures from the wrong assumptions.
        throw new RangeError(`Plan is pinned to assumption version ${wire.setVersionId} but that version was not supplied`);
    }
    return {
        preset: wire.preset,
        ...(pinnedSet !== undefined ? { pinnedSet } : {}),
        ...(firmPresets !== undefined && Object.keys(firmPresets).length > 0 ? { firmPresets } : {}),
        ...(wire.portfolio !== undefined ? { portfolio: wire.portfolio } : {}),
        ...(wire.horizonYears !== undefined ? { horizonYears: wire.horizonYears } : {}),
        ...(wire.overrides !== undefined
            ? {
                overrides: {
                    ...(wire.overrides.inflationBps !== undefined
                        ? { inflationBps: wire.overrides.inflationBps }
                        : {}),
                    ...(wire.overrides.rpiBps !== undefined ? { rpiBps: wire.overrides.rpiBps } : {}),
                    ...(wire.overrides.wageGrowthBps !== undefined
                        ? { wageGrowthBps: wire.overrides.wageGrowthBps }
                        : {}),
                    ...(growthByWrapper !== undefined ? { growthByWrapper } : {}),
                    ...(yieldByWrapper !== undefined ? { yieldByWrapper } : {}),
                    ...(feeByWrapper !== undefined ? { feeByWrapper } : {}),
                    ...(wire.overrides.statePensionEscalation !== undefined
                        ? { statePensionEscalation: wire.overrides.statePensionEscalation }
                        : {}),
                    ...(wire.overrides.customIndices !== undefined
                        ? { customIndices: wire.overrides.customIndices }
                        : {}),
                    ...(wire.overrides.schedule !== undefined
                        ? { schedule: wire.overrides.schedule.map(mapScheduleStep) }
                        : {}),
                },
            }
            : {}),
    };
}
function mapLifeEvent(e) {
    return {
        id: e.id,
        label: e.label,
        year: e.year,
        amount: pence(e.amount),
        direction: e.direction,
        wrapper: e.wrapper,
        ...(e.isGift !== undefined ? { isGift: e.isGift } : {}),
        // A2.1 gift ledger — carried through so the marriage exemption can be
        // applied and the report can name who a gift went to.
        ...(e.giftRecipient !== undefined ? { giftRecipient: e.giftRecipient } : {}),
        ...(e.giftRelationship !== undefined ? { giftRelationship: e.giftRelationship } : {}),
        ...(e.giftOccasion !== undefined ? { giftOccasion: e.giftOccasion } : {}),
        ...(e.exemptionClaimed !== undefined ? { exemptionClaimed: e.exemptionClaimed } : {}),
        ...(e.normalExpenditureNote !== undefined
            ? { normalExpenditureNote: e.normalExpenditureNote }
            : {}),
        ...(e.bprRateBps !== undefined ? { bprRateBps: e.bprRateBps } : {}),
    };
}
/** Strip zod's explicit-undefined optionals and brand the monetary fields per kind. */
function mapPlannedEvent(e) {
    switch (e.kind) {
        case 'schoolFees':
            return {
                kind: e.kind,
                id: e.id,
                label: e.label,
                dependentId: e.dependentId,
                feePerTerm: pence(e.feePerTerm),
                ...(e.presetId !== undefined ? { presetId: e.presetId } : {}),
                ...(e.termsPerYear !== undefined ? { termsPerYear: e.termsPerYear } : {}),
                ...(e.startAge !== undefined ? { startAge: e.startAge } : {}),
                ...(e.startYear !== undefined ? { startYear: e.startYear } : {}),
                ...(e.endAge !== undefined ? { endAge: e.endAge } : {}),
                ...(e.endYear !== undefined ? { endYear: e.endYear } : {}),
                ...(e.escalation !== undefined ? { escalation: e.escalation } : {}),
                ...(e.siblingDiscountBps !== undefined ? { siblingDiscountBps: e.siblingDiscountBps } : {}),
                // A2.4. Without these three the whole item is inert on every real path:
                // the API accepts them, the database stores them, the contract even
                // enforces the s.21 note — and the projection then runs as if none of
                // it were there. `mapping-completeness.test.ts` is what stops the next
                // field being dropped the same way.
                ...(e.paidBy !== undefined ? { paidBy: e.paidBy } : {}),
                ...(e.ihtTreatment !== undefined ? { ihtTreatment: e.ihtTreatment } : {}),
                ...(e.normalExpenditureNote !== undefined
                    ? { normalExpenditureNote: e.normalExpenditureNote }
                    : {}),
                ...(e.exemptionClaimed !== undefined ? { exemptionClaimed: e.exemptionClaimed } : {}),
            };
        case 'university':
            return {
                kind: e.kind,
                id: e.id,
                label: e.label,
                dependentId: e.dependentId,
                annualTuition: pence(e.annualTuition),
                annualMaintenance: pence(e.annualMaintenance),
                ...(e.presetId !== undefined ? { presetId: e.presetId } : {}),
                ...(e.durationYears !== undefined ? { durationYears: e.durationYears } : {}),
                ...(e.startAge !== undefined ? { startAge: e.startAge } : {}),
                ...(e.startYear !== undefined ? { startYear: e.startYear } : {}),
                ...(e.parentalContributionBps !== undefined
                    ? { parentalContributionBps: e.parentalContributionBps }
                    : {}),
                ...(e.escalation !== undefined ? { escalation: e.escalation } : {}),
                ...(e.paidBy !== undefined ? { paidBy: e.paidBy } : {}),
                ...(e.ihtTreatment !== undefined ? { ihtTreatment: e.ihtTreatment } : {}),
                ...(e.normalExpenditureNote !== undefined
                    ? { normalExpenditureNote: e.normalExpenditureNote }
                    : {}),
                ...(e.exemptionClaimed !== undefined ? { exemptionClaimed: e.exemptionClaimed } : {}),
            };
        case 'propertyPurchase':
            return {
                kind: e.kind,
                id: e.id,
                label: e.label,
                year: e.year,
                price: pence(e.price),
                deposit: pence(e.deposit),
                ...(e.ownerId !== undefined ? { ownerId: e.ownerId } : {}),
                ...(e.isPrimaryResidence !== undefined ? { isPrimaryResidence: e.isPrimaryResidence } : {}),
                ...(e.isBuyToLet !== undefined ? { isBuyToLet: e.isBuyToLet } : {}),
                ...(e.rentalYieldBps !== undefined ? { rentalYieldBps: e.rentalYieldBps } : {}),
                ...(e.growthRateBps !== undefined ? { growthRateBps: e.growthRateBps } : {}),
                ...(e.isFirstTimeBuyer !== undefined ? { isFirstTimeBuyer: e.isFirstTimeBuyer } : {}),
                ...(e.isAdditionalDwelling !== undefined
                    ? { isAdditionalDwelling: e.isAdditionalDwelling }
                    : {}),
                mortgageRateBps: e.mortgageRateBps,
                mortgageMonthlyPayment: pence(e.mortgageMonthlyPayment),
                ...(e.mortgageInterestOnly !== undefined
                    ? { mortgageInterestOnly: e.mortgageInterestOnly }
                    : {}),
                ...(e.mortgageEndYear !== undefined ? { mortgageEndYear: e.mortgageEndYear } : {}),
            };
        case 'propertySale':
            return {
                kind: e.kind,
                id: e.id,
                label: e.label,
                year: e.year,
                propertyId: e.propertyId,
                ...(e.sellingCostsBps !== undefined ? { sellingCostsBps: e.sellingCostsBps } : {}),
            };
        case 'businessSale':
            return {
                kind: e.kind,
                id: e.id,
                label: e.label,
                year: e.year,
                assetId: e.assetId,
                ...(e.badrQualifying !== undefined ? { badrQualifying: e.badrQualifying } : {}),
                ...(e.destination !== undefined ? { destination: e.destination } : {}),
            };
        case 'careerBreak':
            return {
                kind: e.kind,
                id: e.id,
                label: e.label,
                personId: e.personId,
                ...(e.startYear !== undefined ? { startYear: e.startYear } : {}),
                ...(e.startAge !== undefined ? { startAge: e.startAge } : {}),
                durationYears: e.durationYears,
                // W1.9: `null` (a cleared box) and absent are the same fact.
                ...(e.redundancyLumpSum != null ? { redundancyLumpSum: pence(e.redundancyLumpSum) } : {}),
            };
        case 'care':
            return {
                kind: e.kind,
                id: e.id,
                label: e.label,
                personId: e.personId,
                ...(e.startAge !== undefined ? { startAge: e.startAge } : {}),
                ...(e.startYear !== undefined ? { startYear: e.startYear } : {}),
                ...(e.durationYears !== undefined ? { durationYears: e.durationYears } : {}),
                annualCost: pence(e.annualCost),
                ...(e.presetId !== undefined ? { presetId: e.presetId } : {}),
                ...(e.escalation !== undefined ? { escalation: e.escalation } : {}),
            };
        case 'recurring':
            return {
                kind: e.kind,
                id: e.id,
                label: e.label,
                amount: pence(e.amount),
                everyNYears: e.everyNYears,
                startYear: e.startYear,
                ...(e.endYear !== undefined ? { endYear: e.endYear } : {}),
                ...(e.direction !== undefined ? { direction: e.direction } : {}),
                ...(e.escalation !== undefined ? { escalation: e.escalation } : {}),
            };
        default: {
            const _exhaustive = e;
            return _exhaustive;
        }
    }
}
function mapMarketShock(s) {
    if (s.anchor !== undefined && s.path !== undefined) {
        return {
            id: s.id,
            label: s.label,
            anchor: s.anchor,
            path: s.path.map((step) => {
                const replace = cleanBpsRecord(step.returnBpsByWrapper);
                const economy = step.economy === undefined
                    ? undefined
                    : Object.fromEntries(Object.entries(step.economy).filter(([, v]) => v !== undefined));
                return {
                    offsetYears: step.offsetYears,
                    returnDeltaBpsByWrapper: cleanBpsRecord(step.returnDeltaBpsByWrapper) ?? {},
                    ...(replace !== undefined ? { returnBpsByWrapper: replace } : {}),
                    ...(economy !== undefined && Object.keys(economy).length > 0 ? { economy } : {}),
                };
            }),
            ...(s.liabilityRatePaths !== undefined
                ? {
                    liabilityRatePaths: s.liabilityRatePaths.map((lp) => ({
                        liabilityId: lp.liabilityId,
                        reamortise: lp.reamortise,
                        steps: lp.steps.map((x) => ({
                            offsetYears: x.offsetYears,
                            interestRateBps: x.interestRateBps,
                        })),
                    })),
                }
                : {}),
        };
    }
    if (s.year === undefined) {
        // Unreachable past the contract's superRefine; fail closed rather than
        // let a malformed shock become "year 0" and a quiet outside-window warning.
        throw new RangeError(`Market shock "${s.label}" has neither a year nor an anchored path`);
    }
    return {
        id: s.id,
        label: s.label,
        year: s.year,
        dropBpsByWrapper: cleanBpsRecord(s.dropBpsByWrapper) ?? {},
    };
}
function mapWindow(w) {
    return {
        ...(w.endYear !== undefined ? { endYear: w.endYear } : {}),
        ...(w.endAtClientAge !== undefined ? { endAtClientAge: w.endAtClientAge } : {}),
    };
}
/**
 * A `jisa` step placed by a `goals[]` record (linked by `goalId`) takes its
 * yearly amount, escalation and end year FROM THE GOAL — the goal is the one
 * source of what the client said; the step is where it sits in the order. A
 * `jisa` step with no resolvable goal (a scenario overlay, or a bare step)
 * runs on its own `annualCap`, flat, until the child turns 18.
 */
function mapWaterfall(w, goals, ctx) {
    return {
        steps: w.steps.map((s) => {
            const goal = s.goalId !== undefined
                ? goals?.find((g) => g.id === s.goalId && g.kind === 'fundFromSurplus')
                : undefined;
            const endYear = goal !== undefined && goal.kind === 'fundFromSurplus' && goal.endAnchor !== undefined
                ? ctx === undefined
                    ? null
                    : resolveGoalAnchorYear(goal.endAnchor, ctx)
                : null;
            const startYear = goal !== undefined && goal.kind === 'fundFromSurplus' && goal.startAnchor !== undefined
                ? ctx === undefined
                    ? null
                    : resolveGoalAnchorYear(goal.startAnchor, ctx)
                : null;
            // A one-off spend is active in its start year only.
            const oneOffEnd = goal !== undefined && goal.kind === 'fundFromSurplus' && goal.oneOff === true
                ? (startYear ?? ctx?.startYear ?? null)
                : null;
            return {
                target: s.target,
                annualCap: goal !== undefined && goal.kind === 'fundFromSurplus'
                    ? pence(goal.amount)
                    : s.annualCap === null
                        ? null
                        : pence(s.annualCap),
                ...(s.targetBalanceMonthsOfSpending !== undefined
                    ? { targetBalanceMonthsOfSpending: s.targetBalanceMonthsOfSpending }
                    : {}),
                ...(s.targetBalance !== undefined
                    ? { targetBalance: s.targetBalance === null ? null : pence(s.targetBalance) }
                    : {}),
                ...(s.dependentId !== undefined ? { dependentId: s.dependentId } : {}),
                ...(s.goalId !== undefined ? { goalId: s.goalId } : {}),
                ...(s.personId !== undefined ? { personId: s.personId } : {}),
                ...(s.liabilityId !== undefined ? { liabilityId: s.liabilityId } : {}),
                ...(s.label !== undefined ? { label: s.label } : {}),
                ...(s.isGift !== undefined ? { isGift: s.isGift } : {}),
                ...(goal !== undefined && goal.kind === 'fundFromSurplus' && goal.escalation !== undefined
                    ? { escalation: goal.escalation }
                    : {}),
                ...(startYear !== null ? { startYear } : {}),
                ...(oneOffEnd !== null ? { endYear: oneOffEnd } : endYear !== null ? { endYear } : {}),
            };
        }),
    };
}
function mapIht(i) {
    return {
        ...(i.residenceToDescendants !== undefined
            ? { residenceToDescendants: pence(i.residenceToDescendants) }
            : {}),
        ...(i.passesToSpouse !== undefined ? { passesToSpouse: i.passesToSpouse } : {}),
        ...(i.hasFullTransferredAllowances !== undefined
            ? { hasFullTransferredAllowances: i.hasFullTransferredAllowances }
            : {}),
        ...(i.charityRateApplies !== undefined ? { charityRateApplies: i.charityRateApplies } : {}),
        ...(i.pensionsOutsideEstate !== undefined
            ? { pensionsOutsideEstate: i.pensionsOutsideEstate }
            : {}),
        ...(i.priorYearExemptionUsed !== undefined
            ? { priorYearExemptionUsed: pence(i.priorYearExemptionUsed) }
            : {}),
        ...(i.residenceBasis !== undefined ? { residenceBasis: i.residenceBasis } : {}),
        ...(i.transferredNilRateBandBps !== undefined
            ? { transferredNilRateBandBps: i.transferredNilRateBandBps }
            : {}),
        ...(i.transferredResidenceNilRateBandBps !== undefined
            ? { transferredResidenceNilRateBandBps: i.transferredResidenceNilRateBandBps }
            : {}),
    };
}
export function buildHouseholdInput(req, options = {}) {
    const { assumptions, snapshot } = resolveAssumptions(buildAssumptionSpec(req.assumptions, options));
    const people = req.people.map((p) => ({
        id: p.id,
        currentAge: p.currentAge,
        retirementAge: p.retirementAge,
        statePensionAge: p.statePensionAge,
        deathAge: p.deathAge,
        taxRegion: p.taxRegion,
        // Spread conditionally, never as an explicit `undefined`: a document saved
        // before this field existed must produce an input object identical to the
        // one it produced then, so the projection re-runs byte-identically.
        ...(p.studentLoans !== undefined
            ? {
                studentLoans: p.studentLoans.map((l) => ({
                    plan: l.plan,
                    firstRepaymentDueYear: l.firstRepaymentDueYear,
                })),
            }
            : {}),
    }));
    const incomeStreams = req.incomeStreams.map((s) => ({
        id: s.id,
        label: s.label,
        ownerId: s.ownerId,
        type: s.type,
        ...(s.sourceAssetId !== undefined ? { sourceAssetId: s.sourceAssetId } : {}),
        amount: pence(s.amount),
        frequency: s.frequency,
        startYear: s.startYear,
        endYear: s.endYear,
        ...(s.startAge !== undefined ? { startAge: s.startAge } : {}),
        ...(s.endAge !== undefined ? { endAge: s.endAge } : {}),
        endsAtRetirement: s.endsAtRetirement,
        growthRateBps: s.growthRateBps,
        taxable: s.taxable,
        // B2.3: dropped here, the plan would tax a trust distribution correctly and
        // say nothing about the tax the trust already paid — the silent half.
        ...(s.fromTrust !== undefined ? { fromTrust: s.fromTrust } : {}),
        // W1.9: `null` (a cleared box) and absent are the same fact.
        ...(s.dbSpouseFractionBps != null ? { dbSpouseFractionBps: s.dbSpouseFractionBps } : {}),
        ...(s.pensionSharingDebitBps != null
            ? { pensionSharingDebitBps: s.pensionSharingDebitBps }
            : {}),
    }));
    const assets = req.assets.map((a) => ({
        id: a.id,
        label: a.label,
        ownerId: a.ownerId,
        wrapper: a.wrapper,
        currentValue: pence(a.currentValue),
        // W1.9: `null` (the builder's cleared box) and absent are the same fact —
        // not recorded. Dropped here so the engine's validator never sees a null,
        // and `cost-basis-missing` speaks for both.
        ...(a.costBasis != null ? { costBasis: pence(a.costBasis) } : {}),
        contribution: pence(a.contribution),
        contributionFrequency: a.contributionFrequency,
        growthRateBps: a.growthRateBps,
        ...(a.yieldBps !== undefined ? { yieldBps: a.yieldBps } : {}),
        ...(a.feeDragBps !== undefined ? { feeDragBps: a.feeDragBps } : {}),
        ...(a.contributionEscalation !== undefined
            ? { contributionEscalation: a.contributionEscalation }
            : {}),
        ...(a.property !== undefined
            ? {
                property: {
                    isBuyToLet: a.property.isBuyToLet,
                    ...(a.property.isPrimaryResidence !== undefined
                        ? { isPrimaryResidence: a.property.isPrimaryResidence }
                        : {}),
                    rentalYieldBps: a.property.rentalYieldBps,
                    linkedLiabilityId: a.property.linkedLiabilityId,
                },
            }
            : {}),
        ...(a.neverDrawFrom !== undefined ? { neverDrawFrom: a.neverDrawFrom } : {}),
        ...(a.contributionStartYear !== undefined
            ? { contributionStartYear: a.contributionStartYear }
            : {}),
        ...(a.contributionEndYear !== undefined ? { contributionEndYear: a.contributionEndYear } : {}),
        // W1.9: `null` (a cleared box) and absent are the same fact.
        ...(a.employerContribution != null
            ? { employerContribution: pence(a.employerContribution) }
            : {}),
        ...(a.employerContributionFrequency !== undefined
            ? { employerContributionFrequency: a.employerContributionFrequency }
            : {}),
        ...(a.accessAge !== undefined ? { accessAge: a.accessAge } : {}),
        ...(a.dcSubType !== undefined ? { dcSubType: a.dcSubType } : {}),
        ...(a.dependentId !== undefined ? { dependentId: a.dependentId } : {}),
        ...(a.bprRateBps !== undefined ? { bprRateBps: a.bprRateBps } : {}),
        ...(a.cgtChattelsExempt !== undefined ? { cgtChattelsExempt: a.cgtChattelsExempt } : {}),
        ...(a.valueUnverified !== undefined ? { valueUnverified: a.valueUnverified } : {}),
        ...(a.notes !== undefined ? { notes: a.notes } : {}),
    }));
    const liabilities = req.liabilities.map((l) => ({
        id: l.id,
        label: l.label,
        kind: l.kind,
        balance: pence(l.balance),
        interestRateBps: l.interestRateBps,
        monthlyPayment: pence(l.monthlyPayment),
        interestOnly: l.interestOnly,
        endYear: l.endYear,
        // FG-F6-04: whose debt it is and what happens on that person's death.
        // Dropped here on the first cut, so every liability behaved as joint and no
        // debt was ever settled — the A2.4 defect, one item later.
        ...(l.ownerId !== undefined ? { ownerId: l.ownerId } : {}),
        ...(l.onDeath !== undefined ? { onDeath: l.onDeath } : {}),
        ...(l.startYear !== undefined ? { startYear: l.startYear } : {}),
        // W1.9: `null` (a cleared box) and absent are the same fact.
        ...(l.annualOverpayment != null ? { annualOverpayment: pence(l.annualOverpayment) } : {}),
        ...(l.rateChanges !== undefined
            ? {
                rateChanges: l.rateChanges.map((c) => ({
                    fromYear: c.fromYear,
                    interestRateBps: c.interestRateBps,
                    ...(c.monthlyPayment !== undefined
                        ? { monthlyPayment: c.monthlyPayment === null ? null : pence(c.monthlyPayment) }
                        : {}),
                    ...(c.interestOnly !== undefined ? { interestOnly: c.interestOnly } : {}),
                })),
            }
            : {}),
    }));
    const spending = req.spending.map((s) => ({
        id: s.id,
        label: s.label,
        ...(s.category !== undefined ? { category: s.category } : {}),
        amount: pence(s.amount),
        frequency: s.frequency,
        startYear: s.startYear,
        endYear: s.endYear,
        inflationLinked: s.inflationLinked,
        // FG-F3-04: whose spending this is. Dropped here on the first cut, which
        // made the owned-row half of the fix inert on every real path.
        ...(s.ownerId !== undefined ? { ownerId: s.ownerId } : {}),
        ...(s.escalation !== undefined ? { escalation: s.escalation } : {}),
        // A2.7 gift ledger on the recurring half of the plan.
        ...(s.isGift !== undefined ? { isGift: s.isGift } : {}),
        ...(s.giftRecipient !== undefined ? { giftRecipient: s.giftRecipient } : {}),
        ...(s.exemptionClaimed !== undefined ? { exemptionClaimed: s.exemptionClaimed } : {}),
        ...(s.normalExpenditureNote !== undefined
            ? { normalExpenditureNote: s.normalExpenditureNote }
            : {}),
    }));
    const lifeEvents = (req.lifeEvents ?? []).map(mapLifeEvent);
    const plannedEvents = (req.plannedEvents ?? []).map(mapPlannedEvent);
    return {
        people,
        ...(req.dependents !== undefined
            ? {
                dependents: req.dependents.map((d) => ({
                    id: d.id,
                    // The wire accepts either the new `name` or the legacy `label`
                    // (EX-1: no backfill). The engine keeps taking exactly one string,
                    // so the tolerance stops here at the boundary rather than leaking
                    // a two-shape type into the projection.
                    label: dependentName(d),
                    // Spec 2026-09-17 §5 (N20): a child known only by an age carries
                    // `ageEvidence`, not a date of birth. The engine keeps taking a
                    // date, so the ADAPTER derives the disclosed modelling date —
                    // anchored to the evidence's own date, never the run's, so moving
                    // the plan's as-of date cannot reinterpret what was said. A stated
                    // date of birth always wins (every pre-existing document has one,
                    // so no existing output moves).
                    dateOfBirth: isoDate(dependentModellingDateOfBirth(d)),
                })),
            }
            : {}),
        incomeStreams,
        assets,
        liabilities,
        spending,
        lifeEvents,
        plannedEvents,
        ...(req.marketShocks !== undefined
            ? { marketShocks: req.marketShocks.map(mapMarketShock) }
            : {}),
        assumptions,
        assumptionsSnapshot: snapshot,
        ...(req.window !== undefined ? { window: mapWindow(req.window) } : {}),
        ...(req.waterfall !== undefined
            ? {
                waterfall: mapWaterfall(req.waterfall, req.goals, {
                    startYear: req.startYear,
                    people: req.people,
                    dependents: (req.dependents ?? []).map((d) => ({
                        id: d.id,
                        dateOfBirth: dependentModellingDateOfBirth(d),
                    })),
                }),
            }
            : {}),
        ...(req.withdrawalWaterfall !== undefined
            ? { withdrawalWaterfall: req.withdrawalWaterfall }
            : {}),
        ...(req.iht !== undefined ? { iht: mapIht(req.iht) } : {}),
        ...(req.survivorSpendingFactorBps !== undefined
            ? { survivorSpendingFactorBps: req.survivorSpendingFactorBps }
            : {}),
        ...(req.giaCgtMode !== undefined ? { giaCgtMode: req.giaCgtMode } : {}),
        // B2.1: carried through so the engine can NAME the arrangements. Dropping
        // it here is the defect A2.4 and A2.5a each shipped once — a field the
        // contract accepts, the database stores, and the projection never sees.
        ...(req.trusts !== undefined
            ? {
                trusts: req.trusts.map((t) => ({
                    id: t.id,
                    label: t.label,
                    kind: t.kind,
                    role: t.role,
                    ...(t.note !== undefined ? { note: t.note } : {}),
                })),
            }
            : {}),
    };
}
/** Strip explicitly-undefined person-age entries (zod optionals). */
function cleanAges(record) {
    if (record === undefined)
        return undefined;
    const out = {};
    for (const [k, v] of Object.entries(record)) {
        if (v !== undefined)
            out[k] = v;
    }
    return out;
}
/**
 * Wire scenario overlay → engine `ScenarioOverlay` (Stage 6). Deltas only:
 * the base household is mapped once by `buildHouseholdInput` and shared; the
 * engine's `resolveScenario` merges each overlay at run time.
 */
export function buildScenarioOverlay(wire, options = {}) {
    const retirementAges = cleanAges(wire.retirementAges);
    const deathAges = cleanAges(wire.deathAges);
    const ovGrowth = cleanBpsRecord(wire.assumptionOverrides?.growthByWrapper);
    const ovYield = cleanBpsRecord(wire.assumptionOverrides?.yieldByWrapper);
    const ovFee = cleanBpsRecord(wire.assumptionOverrides?.feeByWrapper);
    return {
        id: wire.id,
        label: wire.label,
        ...(wire.assumptions !== undefined
            ? { assumptionSpec: buildAssumptionSpec(wire.assumptions, options) }
            : {}),
        ...(wire.assumptionOverrides !== undefined
            ? {
                assumptionOverrides: {
                    ...(wire.assumptionOverrides.inflationBps !== undefined
                        ? { inflationBps: wire.assumptionOverrides.inflationBps }
                        : {}),
                    ...(wire.assumptionOverrides.rpiBps !== undefined
                        ? { rpiBps: wire.assumptionOverrides.rpiBps }
                        : {}),
                    ...(wire.assumptionOverrides.wageGrowthBps !== undefined
                        ? { wageGrowthBps: wire.assumptionOverrides.wageGrowthBps }
                        : {}),
                    ...(ovGrowth !== undefined ? { growthByWrapper: ovGrowth } : {}),
                    ...(ovYield !== undefined ? { yieldByWrapper: ovYield } : {}),
                    ...(ovFee !== undefined ? { feeByWrapper: ovFee } : {}),
                    ...(wire.assumptionOverrides.statePensionEscalation !== undefined
                        ? { statePensionEscalation: wire.assumptionOverrides.statePensionEscalation }
                        : {}),
                    ...(wire.assumptionOverrides.schedule !== undefined
                        ? { schedule: wire.assumptionOverrides.schedule.map(mapScheduleStep) }
                        : {}),
                },
            }
            : {}),
        ...(retirementAges !== undefined ? { retirementAges } : {}),
        ...(deathAges !== undefined ? { deathAges } : {}),
        ...(wire.addLifeEvents !== undefined
            ? { addLifeEvents: wire.addLifeEvents.map(mapLifeEvent) }
            : {}),
        ...(wire.removeLifeEventIds !== undefined
            ? { removeLifeEventIds: wire.removeLifeEventIds }
            : {}),
        ...(wire.addPlannedEvents !== undefined
            ? { addPlannedEvents: wire.addPlannedEvents.map(mapPlannedEvent) }
            : {}),
        ...(wire.removePlannedEventIds !== undefined
            ? { removePlannedEventIds: wire.removePlannedEventIds }
            : {}),
        ...(wire.addMarketShocks !== undefined
            ? { addMarketShocks: wire.addMarketShocks.map(mapMarketShock) }
            : {}),
        ...(wire.removeMarketShockIds !== undefined
            ? { removeMarketShockIds: wire.removeMarketShockIds }
            : {}),
        ...(wire.window !== undefined ? { window: mapWindow(wire.window) } : {}),
        ...(wire.waterfall !== undefined ? { waterfall: mapWaterfall(wire.waterfall) } : {}),
        ...(wire.withdrawalWaterfall !== undefined
            ? { withdrawalWaterfall: wire.withdrawalWaterfall }
            : {}),
        ...(wire.iht !== undefined ? { iht: mapIht(wire.iht) } : {}),
        ...(wire.giaCgtMode !== undefined ? { giaCgtMode: wire.giaCgtMode } : {}),
        ...(wire.taxOverrides !== undefined ? { taxOverrides: wire.taxOverrides } : {}),
    };
}
//# sourceMappingURL=wire-mapping.js.map